import { JSX, useContext, useEffect, useMemo, useRef, useState } from "react";
import { ColumnConfig } from "@app/shared/waffles";
import { Waffles } from "@app/shared/waffles/Waffles";
import { BulkProviderChangeProjectTableData, ProjectView } from "@app/shared/provider-change-wizards/interfaces";
import PageSection from "@app/shared/PageSection";
import { QESAlert } from "@app/shared/qes-standards/Alert";
import { BulkTargetingWizardContext } from "@app/targeting/import/TargetingWizardContext";
import { BulkProjectTypeEnum, BulkTargetingWizardStepProps } from "@app/targeting/import/interfaces";
import { TagGroupData, useSelectProjectByTags } from "@app/shared/provider-change-wizards/useSelectProjectsByTag";
import { ProjectOrTagSearchBox } from "@app/shared/provider-change-wizards/ProjectOrTagSearchBox";

const countyProjectsTableColumnConfig: ColumnConfig = {
  details: {
    displayText: "Details",
    testId: "details",
  },
  numberOfProjects: {
    displayText: "Number of Projects",
    testId: "number-of-projects",
  },
  projectId: {
    displayText: "",
    hidden: true,
    testId: "",
  },
  projectName: {
    displayText: "Project Name",
    testId: "project-name",
  },
  rowIsDisabled: {
    displayText: "",
    hidden: true,
    testId: "",
  },
  tagGroupName: {
    displayText: "Tag Group",
    testId: "tag-group",
  },
  tagName: {
    displayText: "Tag",
    noSort: true,
    testId: "tag",
  },
  templateName: {
    displayText: "Template",
    testId: "template",
  },
};

const zipProjectsTableColumnConfig: ColumnConfig = {
  details: {
    displayText: "Details",
    testId: "details",
  },
  numberOfProjects: {
    displayText: "Number of Projects",
    testId: "number-of-projects",
  },
  projectId: {
    displayText: "",
    hidden: true,
    testId: "",
  },
  projectName: {
    displayText: "Project",
    testId: "project-name",
  },
  rowIsDisabled: {
    displayText: "",
    hidden: true,
    testId: "",
  },
  tagGroupName: {
    displayText: "Tag Group",
    testId: "tag-group",
  },
  tagName: {
    displayText: "Tag",
    noSort: true,
    testId: "tag",
  },
  templateName: {
    displayText: "Template",
    testId: "template",
  },
};

const BulkTargetingWizardSelectProjects = (props: BulkTargetingWizardStepProps): JSX.Element => {
  const wizardContext = useContext(BulkTargetingWizardContext);
  const isZip = wizardContext.projectType === BulkProjectTypeEnum.Zip;
  const projectsTableColumnConfig = isZip ? zipProjectsTableColumnConfig : countyProjectsTableColumnConfig;

  const [searchString, setSearchString] = useState("");
  const [filteredView, setFilteredView] = useState<ProjectView>();
  const [view, setView] = useState<ProjectView>("projects");
  const [isLoading, setIsLoading] = useState(false);
  const searchRef = useRef<HTMLInputElement>(null);
  const {
    getEligibleSelectedProjectIds,
    handleTagCheckboxChange,
    indeterminateTags,
    isTagApiCallResolved,
    renderedExpansionData,
    renderedTags,
    selectedTags,
    tagExpansionData,
    tagGroupData,
  } = useSelectProjectByTags(wizardContext.projects, searchString, filteredView, wizardContext.totalSelectedProjectIds, wizardContext.setEligibleSelectedProjectIds, wizardContext.setTotalSelectedProjectIds);

  useEffect(() => {
    // fetch data from api
    setIsLoading(true);
    wizardContext.projectionProviderService.getBulkTargetingProjectsAsync(true, false, undefined, wizardContext.projectType ?? BulkProjectTypeEnum.County).then((response) => {
      const tabledata: BulkProviderChangeProjectTableData[] = response.map((project) => {
        const detailsArr: string[] = [];
        if (project.isReadOnly) {
          detailsArr.push("Read Only");
        }
        if (project.hasExclusions) {
          detailsArr.push("Existing Excluded Providers");
        }
        if (project.hasTargets) {
          detailsArr.push("Existing Targeted Providers");
        }
        if (!project.hasMarketOrProspect) {
          detailsArr.push("No Market/Prospect Network");
        }
        const detailsStr = detailsArr.join(" And ");
        return {
          /* oxlint-disable sort-keys */
          rowIsDisabled: project.isReadOnly || !project.hasMarketOrProspect,
          key: project.projectId.toString(),
          projectId: project.projectId,
          projectName: project.projectName,
          templateName: project.templateName,
          details: detailsStr,
          /* oxlint-enable sort-keys */
        };
      });
      wizardContext.setProjects(tabledata);
      setIsLoading(false);
    });
  }, []);

  const renderedProjects = useMemo(() => {
    if (!searchString) {
      return wizardContext.projects.sort((a, b) => a.projectName > b.projectName ? 1 : -1);
    }
    const regex = new RegExp(searchString, "i");
    if (filteredView === "projects") {
      const filteredProjectList = wizardContext.projects.filter((project) => !!project.projectName.match(regex));
      return filteredProjectList.sort((a, b) => a.projectName > b.projectName ? 1 : -1);
    } else if (filteredView === "tags") {
      let filteredProjectNames: string[] = [];
      tagExpansionData.forEach((value) => {
        filteredProjectNames = filteredProjectNames.concat(...value.filter((v) => v.hiddenTagGroupName.match(regex) || v.tagName.match(regex)).map((v) => v.projectNames));
      })
      filteredProjectNames = [...new Set(filteredProjectNames)];
      return wizardContext.projects.filter((p) => filteredProjectNames.includes(p.projectName));
    }
  }, [searchString, wizardContext.projects, filteredView, tagExpansionData]);

  const handleProjectCheckboxChange = (selectedKeys: string[], isBulk?: boolean) => {
    if (isBulk) {
      let newSelectedProjectIds = [...wizardContext.totalSelectedProjectIds];
      if (selectedKeys.length > 0) {
        newSelectedProjectIds.push(...selectedKeys);
      } else {
        const removedIds = renderedProjects.map((project) => project.projectId.toString());
        newSelectedProjectIds = newSelectedProjectIds.filter((id) => { return !removedIds.includes(id); });
      }
      const newTotalSelectedProjectIds = [...new Set(newSelectedProjectIds)];
      wizardContext.setTotalSelectedProjectIds(newTotalSelectedProjectIds);
      wizardContext.setEligibleSelectedProjectIds(getEligibleSelectedProjectIds(newTotalSelectedProjectIds));
    } else {
      wizardContext.setTotalSelectedProjectIds(selectedKeys);
      wizardContext.setEligibleSelectedProjectIds(getEligibleSelectedProjectIds(selectedKeys));
    }
  };

  useEffect(() => {
    props.stepIsValid(wizardContext.eligibleSelectedProjectIds.length > 0);
  }, [props.stepIsValid, wizardContext.eligibleSelectedProjectIds]);

  const updateSearchString = (newString: string) => {
    setSearchString(newString);
    setFilteredView(newString ? view : null);
  }

  return (
    <PageSection>
      <div className="container mt-3">
        <p className="copy-text-default">Select the projects you would like to target from.</p>
        <QESAlert type="info" classNames="mb-2" persist={true}>
          Any project that has current targets or excludes will be overwritten if selected. Readonly projects can not be selected.
        </QESAlert>

        <ProjectOrTagSearchBox
          filteredView={filteredView}
          inputRef={searchRef}
          searchString={searchString}
          setView={setView}
          updateSearchString={updateSearchString}
          view={view}
        />
      </div>
      <div className="container">
        <div className="row">
          <div className="col-12">
            <p>{wizardContext.eligibleSelectedProjectIds.length} eligible {wizardContext.eligibleSelectedProjectIds.length === 1 ? "project" : "projects"} selected</p>
            {view === "projects" &&
              <Waffles<BulkProviderChangeProjectTableData>
                columns={projectsTableColumnConfig}
                data={renderedProjects}
                fullWidth={true}
                loading={isLoading}
                handleCheckboxChange={handleProjectCheckboxChange}
                selectedKeys={wizardContext.eligibleSelectedProjectIds}
                showCheckboxes={true}
                testId="import-targeting-projects-table"
                totalNumberOfRecords={wizardContext.projects.length}
              />
            }

            {view === "tags" &&
              <Waffles<TagGroupData>
                columns={projectsTableColumnConfig}
                data={renderedTags}
                dataExpansions={renderedExpansionData}
                fullWidth={true}
                loading={!isTagApiCallResolved}
                handleCheckboxChange={handleTagCheckboxChange}
                selectedKeys={selectedTags}
                showCheckboxes={true}
                testId="select-projects__tags-table"
                totalNumberOfRecords={tagGroupData.length}
                showExpansionCheckboxesOnly={true}
                dataExpansionsHaveIndividualCheckboxes={true}
                indeterminateKeys={indeterminateTags}
              />
            }
          </div>
        </div>
      </div>
    </PageSection>
  );
};

export default BulkTargetingWizardSelectProjects;
